import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  Box,
  Checkbox,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { HttpContext } from '../../context/HttpContext';
import { getErrors, getMessage } from '../../util';

const CostCategoryForm = ({ onClose, onSuccess, category }) => {
  const isEdit = !!category;
  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .max(20, 'Please enter 20 characters or less')
      .required('This is a mandatory field'),
    name: Yup.string()
      .max(255, 'Please enter 255 characters or less')
      .required('This is a mandatory field'),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: isEdit
      ? { code: category.code, name: category.name, enabled: category.enabled }
      : { code: '', name: '', enabled: true },
  };

  const { register, handleSubmit, formState, setError } = useForm(formOptions);
  const { errors, isSubmitting, isDirty, isValid } = formState;
  const toast = useToast();
  const { authAxios } = useContext(HttpContext);

  const onSubmit = async (values) => {
    try {
      const data = {
        code: values.code,
        name: values.name,
        enabled: values.enabled,
      };

      let res;
      if (isEdit) {
        res = await authAxios.patch(`/cost-categories/${category.id}`, data);
      } else {
        res = await authAxios.post(`/cost-categories`, data);
      }

      onSuccess(res.data);
      onClose();
    } catch (onError) {
      const apiErrors = getErrors(onError);
      if (apiErrors) {
        if (apiErrors.code && apiErrors.code.errors && apiErrors.code.errors.length) {
          setError('code', { type: 'server', message: apiErrors.code.errors[0] });
        }
        if (apiErrors.name && apiErrors.name.errors && apiErrors.name.errors.length) {
          setError('name', { type: 'server', message: apiErrors.name.errors[0] });
        }
      } else {
        const message = getMessage(onError);
        if (message) {
          toast({
            position: 'top-left',
            description: message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl isInvalid={errors.code} mb={[4, 4, 8]}>
        <FormLabel>Nominal Account Code</FormLabel>
        <Input
          name="code"
          id="code"
          placeholder="Enter nominal code (e.g. 6204)"
          focusBorderColor="black"
          errorBorderColor="error.500"
          {...register('code')}
        />
        <FormErrorMessage>{errors.code?.message || errors.code}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={errors.name} mb={[4, 4, 8]}>
        <FormLabel>Name</FormLabel>
        <Input
          name="name"
          id="name"
          placeholder="Enter category name"
          focusBorderColor="black"
          errorBorderColor="error.500"
          {...register('name')}
        />
        <FormErrorMessage>{errors.name?.message || errors.name}</FormErrorMessage>
      </FormControl>

      <FormControl mb={[4, 4, 8]}>
        <Checkbox
          colorScheme="brand"
          defaultChecked={isEdit ? category.enabled : true}
          {...register('enabled')}
        >
          Enabled
        </Checkbox>
      </FormControl>

      <Box d="flex" justifyContent="flex-end" mt={20} mb={20}>
        <Button
          type="submit"
          size="lg"
          colorScheme="brand"
          disabled={!isDirty || !isValid || isSubmitting}
          isLoading={isSubmitting}
          textTransform="initial"
          _hover={{ bg: '#000' }}
          borderRadius="10px"
          mr="10px"
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
        <Button
          variant="ghost"
          size="lg"
          colorScheme="brand"
          disabled={isSubmitting}
          textTransform="initial"
          borderRadius="10px"
          onClick={onClose}
        >
          Cancel
        </Button>
      </Box>
    </form>
  );
};

export default CostCategoryForm;
